import { useTranslation } from 'react-i18next';

import { TableCell, Typography } from '@mui/material';

import { noop } from '@altirllc/shared/utils/functionalUtils';

import { fromTemplate } from '@/utils/arrayUtils';

import type { IOnSort, TSort } from '@/components/filter/types';

import type { TBaseCellProps, THeading } from '../types';
import { HeadLabel } from './HeadLabel';

export type THeadCellProps = TBaseCellProps & {
  value: THeading | THeading[];
  onSort?: IOnSort;
  sort?: TSort;
  alwaysShowIcon?: boolean;
};

/**
 * What: one header cell with one heading, or several headings joined by " | ".
 * Why: the header is where a column is named and sorted.
 * Constraints: renders `scope="col"`; the `th` carries `aria-sort` (via MUI `sortDirection`) only
 * when it is the sorted column (for several headings: when any of them is the sorted one);
 * unsorted columns get no `aria-sort`.
 */
export const HeadCell = ({
  value,
  sort,
  onSort = noop,
  alwaysShowIcon,
  ...props
}: THeadCellProps) => {
  const { t } = useTranslation();

  const values = Array.isArray(value) ? value : [value];
  const isSorted = !!sort && values.some((heading) => heading.sortKey === sort.key);

  const headings = values.map((heading, index) => (
    <HeadLabel
      key={heading.label}
      label={t(heading.label)}
      sortKey={heading.sortKey}
      leftIcon={index === 0 && values.length > 1}
      sorted={sort && sort.key === heading.sortKey && sort.direction}
      onSort={onSort}
      alwaysShowIcon={alwaysShowIcon}
    />
  ));

  const separators = fromTemplate(values.length - 1, (index) => (
    <Typography key={`separator-${index}`} variant="caption" color="inherit" fontWeight="inherit">
      {' | '}
    </Typography>
  ));

  const blocks = headings.reduce(
    (memo, heading, index) => [...memo, heading, separators[index]],
    [] as JSX.Element[],
  );

  return (
    <TableCell
      variant="head"
      scope="col"
      sortDirection={isSorted && sort ? sort.direction : false}
      {...props}
    >
      {blocks}
    </TableCell>
  );
};
